/**
 * Helpers for reading fetch responses safely.
 *
 * `response.json()` throws `SyntaxError: Unexpected end of JSON input` when the
 * body is empty — which is what the dev server returns for `/api/*` when the
 * Pages Functions are not running: an empty 404 from Vite itself, or an empty
 * 500 from Vite's `/api` proxy when nothing is listening on port 8788.
 * That raw SyntaxError used to surface to the user instead of a real message.
 */

/**
 * Reads a response body as JSON without throwing on empty or non-JSON payloads
 *
 * @param response - The fetch response to read
 * @returns Parsed body, or null when the body is empty or is not valid JSON
 */
export const read_json_body = async <T>(response: Response): Promise<T | null> => {
  const text = await response.text().catch(() => '');

  if (!text.trim()) {
    return null;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
};

/**
 * Builds a readable message for a failed response that carried no JSON error
 *
 * A 404, or a 5xx with no JSON body, means the request never reached a Pages
 * Function: Vite answers `/api/*` itself when the Functions server is down.
 * Real function errors always carry a JSON `error` field, so they never land
 * here.
 *
 * @param response - The failed fetch response
 * @param has_json_body - Whether the response carried a parsable JSON body
 * @returns Message safe to show to the user
 */
export const http_error_message = (response: Response, has_json_body = true): string => {
  const is_api_unreachable = response.status === 404 || (!has_json_body && response.status >= 500);

  if (is_api_unreachable) {
    return import.meta.env.DEV
      ? 'API is not running. Start it with "bun run dev:full" instead of "bun dev".'
      : 'Service is unavailable. Please try again later.';
  }

  return `Server error (${response.status}). Please try again.`;
};
